<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { useToasteo } from '@deegital/vue-trustup-io-toasteo';
    import { useCobrowseRequestListener } from '~/composables/cobrowse/useCobrowseRequestListener';
    import { useCobrowseStore } from '~/stores/cobrowseStore';

    const cobrowse = useCobrowseStore();
    const { pendingRequest, endedSession, acceptOutcome } = storeToRefs(cobrowse);
    const { t } = useTranslation();
    const toasteo = useToasteo();
    const isAnswering = ref(false);

    useCobrowseRequestListener();

    const isOpen = computed({
        get: () => !!pendingRequest.value,
        set: (open) => {
            if (!open && !isAnswering.value) {
                decline();
            }
        },
    });

    async function accept(): Promise<void> {
        isAnswering.value = true;

        try {
            await cobrowse.acceptRequest();
        } catch {
            useToasteoError('cobrowse.toasts.join_failed');
        } finally {
            isAnswering.value = false;
        }
    }

    async function decline(): Promise<void> {
        isAnswering.value = true;

        try {
            await cobrowse.declineRequest();
        } catch {
            useToasteoError();
        } finally {
            isAnswering.value = false;
        }
    }

    watch(acceptOutcome, (outcome) => {
        if (outcome?.kind === 'started') {
            toasteo.success({ text: t('cobrowse.toasts.started', { name: outcome.agentName }) });
        } else if (outcome?.kind === 'failed') {
            useToasteoError('cobrowse.toasts.join_failed');
        }
    });

    watch(endedSession, (session) => {
        if (session && session.reason !== 'declined-by-client') {
            useToasteoInfo('cobrowse.toasts.ended');
        }
    });
</script>

<template>
    <UiActionSheet
        v-model:open="isOpen"
        :title="t('cobrowse.request.title')"
    >
        <template #header>
            <h2 class="hidden px-6 pr-12 pt-6 font-bricolage text-lg font-bold text-branding-rich-black lg:block">
                {{ t('cobrowse.request.title') }}
            </h2>
        </template>
        <div
            v-if="pendingRequest"
            data-testid="cobrowse-request"
            class="flex flex-col gap-6 px-4 pb-6 pt-2 lg:px-6 lg:pt-3"
        >
            <p class="text-sm text-statistics-gray">
                {{ t('cobrowse.request.text', { name: pendingRequest.agentName }) }}
            </p>
            <div class="flex flex-col gap-2.5">
                <UiAction
                    data-testid="cobrowse-request-accept"
                    :content="{ label: 'cobrowse.request.accept', type: 'brand', icon: 'fa-regular fa-check' }"
                    modifier="dsFullLg"
                    shape="softLg"
                    :is-loading="isAnswering"
                    @click="accept"
                />
                <UiAction
                    data-testid="cobrowse-request-decline"
                    :content="{ label: 'cobrowse.request.decline', type: 'surface' }"
                    modifier="dsFullLg"
                    shape="softLg"
                    :is-disabled="isAnswering"
                    @click="decline"
                />
            </div>
        </div>
    </UiActionSheet>
</template>
